import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {Typography} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import {CareTaskFeature} from '../../../backend/enums/care-task-feature';
import {CareTaskNotDoneReason} from '../../../backend/enums/care-task-not-done-reason';
import {AttachmentWithFile} from '../../../backend/interfaces/attachment';
import {ChangeHistoryValues} from '../../../backend/interfaces/care-task';
import {BloodGlucoseData, SleepHealthDataType} from '../../../backend/interfaces/care-task-data';
import {translations} from '../../../i18n/translations';

/**
 * This componenent renders a change field in the task logs.
 *
 * @example Each LINE is a FIELD
 * ```txt
 * Estado: Não Realizado
 * Motivo de não realização: Ausência do utente
 * Notas: Lorem ipsum dolor sit amet.
 * Temperatura: 36.5 ºC
 * Ficheros adicionados: exame.pdf, imagem.png
 * Ficheros removidos: exame_antigo.pdf
 * ```
 */
export function TaskLogChangeField({
  change,
  feature,
  ...rest
}: {
  change: ChangeHistoryValues;
  feature?: CareTaskFeature;
  key?: string;
}) {
  const {t} = useTranslation();

  if (change.newValue === undefined || change.newValue === null) {
    // Skip new changes that hold no value
    return null;
  }

  switch (change.field) {
    case 'markedAsDone': {
      let markedAsDone = change.newValue as Boolean;

      return (
        <div className="status" {...rest}>
          <Typography className="label">{`${t(translations.care_and_activities.handle_task_modal.history.status)}:`}</Typography>
          <Typography className={`${markedAsDone ? 'done' : 'not-done'} desc`}>
            {markedAsDone ? <CheckIcon /> : <ClearIcon />}
            {`${t(translations.care_and_activities.handle_task_modal.task_status[markedAsDone ? 'done' : 'not_done'])}`}
          </Typography>
        </div>
      );
    }

    case 'taskNotDoneReason': {
      let reason = change.newValue as CareTaskNotDoneReason;

      return (
        <div className="status" {...rest}>
          <Typography className="label">
            {t(translations.care_and_activities.handle_task_modal.history.task_not_done_reason)}:
          </Typography>
          <Typography className="desc">
            {t(translations.care_taks_not_done_reasons[reason])}
          </Typography>
        </div>
      );
    }

    case 'notes': {
      let notes = change.newValue as string;

      return (
        <div className="status" {...rest}>
          <Typography className="label">
            {t(translations.care_and_activities.handle_task_modal.history.notes)}:
          </Typography>
          <Typography className="desc">{notes}</Typography>
        </div>
      );
    }

    case 'attachments': {
      let newAttachments = change.newValue as Array<AttachmentWithFile>;
      let oldAttachments = (change.oldValue as Array<AttachmentWithFile>) ?? [];
      let addedFiles: AttachmentWithFile[] = [];
      let removedFiles: AttachmentWithFile[] = [];

      // Check for possible added files
      addedFiles = newAttachments.filter(
        (newFile) =>
          !oldAttachments.some((oldFile) => newFile.data?.fileUrl === oldFile.data?.fileUrl)
      );

      // Check for possible removed files
      removedFiles = oldAttachments.filter(
        (oldFile) =>
          !newAttachments.some((newFile) => oldFile.data?.fileUrl === newFile.data?.fileUrl)
      );

      return (
        <>
          {addedFiles.length > 0 && (
            <div className="status" {...rest}>
              <Typography className="label">
                {t(translations.care_and_activities.handle_task_modal.history.files_added)}:
              </Typography>
              <Typography className="desc">
                {addedFiles
                  .map((file) => {
                    return (
                      file.name ?? file.data?.fileUrl.slice(file.data?.fileUrl.lastIndexOf('/') + 1)
                    );
                  })
                  .join(', ')}
              </Typography>
            </div>
          )}

          {removedFiles.length > 0 && (
            <div className="status" {...rest}>
              <Typography className="label">
                {t(translations.care_and_activities.handle_task_modal.history.files_removed)}:
              </Typography>
              <Typography className="desc">
                {removedFiles
                  .map((file) => {
                    return (
                      file.name ?? file.data?.fileUrl.slice(file.data?.fileUrl.lastIndexOf('/') + 1)
                    );
                  })
                  .join(', ')}
              </Typography>
            </div>
          )}
        </>
      );
    }

    case 'data': {
      const values = change.newValue as unknown;
      if (!feature) {
        console.warn('History > No feature provided for the data values:', change);
        return null;
      }

      const str = getFieldDataTextByFeature(t, values, feature);

      if (str === null) {
        return null;
      }

      return (
        <div className="status" {...rest}>
          <Typography className="label">
            {t(translations.care_and_activities.handle_task_modal.feature_input.label[feature])}:
          </Typography>
          <Typography className="desc">{str}</Typography>
        </div>
      );
    }

    case 'executionDatetime': {
      // Do nothing. Date is handled in the parent component.
      return null;
    }

    default: {
      console.log('TaskLogs > Unsupported field type:', change);
      return null;
    }
  }
}

// ------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------

function getFieldDataTextByFeature(
  t: TFunction,
  values: any,
  feature?: CareTaskFeature
): string | null {
  switch (feature) {
    case CareTaskFeature.BloodOxygenHealthMeasurement:
      return values.percentage + ' %';
    case CareTaskFeature.BloodPressureHealthMeasurement:
      return values.systolic + ' - ' + values.diastolic + ' mmHg';
    case CareTaskFeature.RespiratoryRateHealthMeasurement:
      return (
        values.breathsPerMinute + ' ' + t(translations.health_measurements.units.breathsPerMinute)
      );
    case CareTaskFeature.BloodGlucoseHealthMeasurement:
      return `${values.value} mg/dL (${t(translations.care_and_activities.handle_task_modal.feature_input.inputs.mealTime.options[values.mealTime as BloodGlucoseData['mealTime']])?.toLowerCase()})`;
    case CareTaskFeature.BodyFatPercentageHealthMeasurement:
      return values.percentage + ' %';
    case CareTaskFeature.BodyMusclePercentageHealthMeasurement:
      return values.percentage + ' %';
    case CareTaskFeature.BodyWaterPercentageHealthMeasurement:
      return values.percentage + ' %';
    case CareTaskFeature.StepsHealthMeasurement:
      return values.value;
    case CareTaskFeature.SleepHealthMeasurement:
      return `${values.value} ${t(translations.time_units.minutes)} (${t(translations.care_and_activities.handle_task_modal.feature_input.inputs.type.options[values.type as SleepHealthDataType])?.toLowerCase()})`;
    case CareTaskFeature.BodyHeightHealthMeasurement:
      return values.heightInCentimeters + ' cm';
    case CareTaskFeature.HeartRateHealthMeasurement:
      return values.beatsPerMinute + ' ' + t(translations.health_measurements.units.beatsPerMinute);
    case CareTaskFeature.NatriureticPeptideTestHealthMeasurement:
      return values.bnp + ' - ' + values.nt_proBnp + ' pg/mL';
    case CareTaskFeature.BodyTemperatureHealthMeasurement:
      return values.temperatureInCelsius + ' ºC';
    case CareTaskFeature.BodyWeightHealthMeasurement:
      return values.weightInKilograms + ' Kg';

    default:
      console.log('TaskLogs > Unsupported data feature:', feature);
      return null;
  }
}
